import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { IFieldEditorPromptGroup } from '@/shared/types/fieldEditor';
import { DELETE_DEAL_ASSISTANT_AI_PROMPT_GROUP_MUTATION } from '@/shared/ui/fieldPromptEditorModalPage/queries';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

interface DeleteAllPromptsProps {
  isOpen: boolean;
  fieldName: string;
  promptGroupId: string;
  refetch: () => Promise<ApolloQueryResult<any>>;
  savePromptGroup?: (promptGroup: IFieldEditorPromptGroup | null) => void;
  closeFieldPromptEditorModalPage: () => void;
  onClose: () => void;
}

const DeleteAllPrompts = ({
  isOpen,
  fieldName,
  promptGroupId,
  refetch,
  savePromptGroup,
  closeFieldPromptEditorModalPage,
  onClose
}: DeleteAllPromptsProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [deletePromptGroup] = useMutation(DELETE_DEAL_ASSISTANT_AI_PROMPT_GROUP_MUTATION);

  const saveValue = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      await deletePromptGroup({
        variables: {
          id: promptGroupId
        }
      });

      if (savePromptGroup) {
        savePromptGroup(null);
      }
      await refetch();
      closeFieldPromptEditorModalPage();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      confirmButton={{ name: 'Confirm', onClick: saveValue, disabled: loading }}
    >
      <ModalContainer>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {loading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>
              Please confirm you want to delete all Prompt Settings for <span style={{ fontWeight: '700' }}>{fieldName}</span>.
            </ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>
              Data will be permanently discarded upon deletion. Fields without prompt settings are ignored by the AI Assistant.
            </ModalDescription>
          </>
        )}
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default DeleteAllPrompts;

const ModalContainer = styled.div`
  font-weight: 400;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ModalTitle = styled.div`
  font-size: 33px;
  font-weight: 400;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 22.4px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;
